// @flow
import * as React from 'react';
import './leaflet_';
import {
    Map, FeatureGroup, Polygon, LeafletConsumer, TileLayer,
} from 'react-leaflet';
import { defineMessages, injectIntl, type IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import type { Match } from 'react-router-dom';
import { Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import type { Confirmation } from 'speed-js-react';
import {
    connectAPI, typeof ApiClient, withRouter, withConfirmation,
} from 'speed-js-react';
import MessageDialog from 'esm-web-pro/components/dialogs/MessageDialog';

import Spinner from 'esm-web-pro/components/forms/Spinner';
import { SidePane } from 'esm-web-pro/components/layout';
import { ApiForm } from 'esm-web-pro/containers/forms';
import MapSidePaneContainer from 'esm-web-pro/containers/map/sidepane/MapSidePaneContainer';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import * as sidePanelActionCreators from 'esm-web-pro/redux/side-panel/actions';
import type { SidePanel } from 'esm-web-pro/redux/side-panel/reducer';
import EditorResourceSelector from './EditorResourceSelector';
import ZoomRestricted from './ZoomRestricted';
import DrawControl from './controls/DrawControl';
import '../../styles/map/editor.less';
import type { Resource } from './types';
import ZoomControl from './controls/ZoomControl';


type Props = {
    center: [number, number],
    zoom: number,
    resources: Array<Resource>,
    getData: (resources: Array<Resource>, bbox: string, queries: Object) => Promise<Object>,
    updateObject: (resource: Resource, id: string, value: Object) => Promise<Object>,
    deleteObject: (resource: Resource, id: string) => Promise<Object>,
    intl: IntlShape,
    history: { push: (location: string) => void },
    match: Match,
    confirm: (confirmation: Confirmation) => void,
    bounds?: Array<[number, number]>,
    setPosition: (center, zoom) => {},
    setMapLoading: (mapLoading: boolean) => {},
    getCoverageVersions: (resource: Resource, mapUuid: string, identity: string) => Promise<Object>,
    coverageVersions: Object,
    onEditStart: (layer: Object) => void,
    onEditEnd: (layer: Object) => void,

    // SidePanel redux props
    sidePanel: ?SidePanel,
    openPanel: ?(key: string, url: ?string, sensitive: ?(boolean | Object)) => {
        key: string, url: string, sensitive: ?(boolean | Object),
    },
    closePanel: ?(key: string, skipSensitivity: boolean) => { key: string },
    closeTopmost: ?(skipSensitivity: boolean) => {},
    closeAllPanels: ?(skipSensitivity: boolean) => {},
}


type State = {
    creatingObject: {
        layer: Object,
        resource: Resource,
        initial?: Object,
    } | null,
    updatingObject: {
        layer: Object,
        resource: Resource,
    } | null,
    creatingLayer: {
        layer: Object,
        resource: Resource,
    } | null,
    editingLayer: {
        layer: Object,
        resource: Resource,
    } | null,
    drawnLayer: Object,
    enabledResources: { [name: string]: Resource },
    propsResources: Array<Resource>,
    errMessages: string | null,
    drawing: boolean,
    loading: boolean,
    queryLoading: boolean,
    showMapEditForm: boolean,
}


const SRID = 4326; // Also known as WGS84, units are in degrees of longitude and latitude

const geometryType = {
    polygon: 'Polygon',
    polyline: 'Polyline',
    point: 'Point',
    marker: 'Marker',
};

const DRAW_TYPES_SETTINGS = {
    polygon: {
        type: geometryType.polygon,
    },
    polyline: {
        type: geometryType.polyline,
    },
    point: {
        type: geometryType.marker,
    },
};

const messages = defineMessages({
    'create-save': {
        id: 'editor-create-save',
        defaultMessage: 'Save',
    },
    'update-save': {
        id: 'editor-update-save',
        defaultMessage: 'Save',
    },
    error: {
        id: 'editor-error',
        defaultMessage: 'OK',
    },
    add: {
        id: 'editor-add',
        defaultMessage: 'Add',
    },
    question: {
        id: 'editor-confirmation-question',
        defaultMessage: 'Are you sure you want to delete',
    },
    yes: {
        id: 'editor-confirmation-yes',
        defaultMessage: 'Yes',
    },
    no: {
        id: 'editor-confirmation-no',
        defaultMessage: 'No',
    },
    cancelDelete: {
        id: 'delete-line-cancel',
        defaultMessage: 'Cancel',
    },
});

const MAP_SIDE_CONTAINER_KEY = 'MAP_SIDE_CONTAINER';
const CREATE_MAP_SIDE_CONTAINER_KEY = 'CREATE_MAP_SIDE_CONTAINER';

const mapStateToProps = (state, ownProps) => ({
    sidePanel: (() => getSidePanel(state, ownProps.id))(),
});

const mapDispatchToProps = (dispatch) => (bindActionCreators(sidePanelActionCreators, dispatch));

@connect(mapStateToProps, mapDispatchToProps)
@withRouter
@withConfirmation
@connectAPI((apiClient: ApiClient) => ({
    getData: {
        send: (resources: Array<Resource>, bbox: string, queries: Object = {}) => (
            Promise.all(resources.map((r) => {
                const args = r.arguments || [];
                return (
                    get(apiClient.resources, r.name)[r.method || 'list'](
                        ...args, {
                            [r.bboxQueryName || 'in_bbox']: bbox,
                            srid: SRID,
                            ...r.query,
                            ...queries[r.name],
                            type: 'coverage',
                        },
                    ).then((response) => ({
                        results: response.results,
                        resource: r,
                    }))
                );
            }))
        ),
    },
    updateObject: {
        send: (resource: Resource, id: string, value: Object) => {
            const args = resource.arguments || [];
            return get(apiClient.resources, resource.name).partialUpdate(...args, id, {
                [resource.geometryField]: value,
            });
        },
    },
    deleteObject: {
        send: (resource: Resource, id: string) => {
            const args = resource.arguments || [];
            return get(apiClient.resources, resource.name).delete(...args, id);
        },
    },
}), true)
class Editor extends React.Component<Props, State> {
    map: { current: null | Map };

    editControlRefs: { [string]: Object } = {};

    callbacks: {
        [string]: {
            onCreated: Function,
        },
    } = {};

    filters: { [string]: ?Object } = {};

    featureGroups: {
        [string]: { current: null | (React.Node & { leafletElement: Object }) },
    } = {};

    currentEditedResource: ?Resource = null;

    static getDerivedStateFromProps(props: Props, state: State) {
        let newState = null;
        if (props.resources !== state.propsResources) {
            newState = { enabledResources: {}, propsResources: props.resources };
            props.resources.forEach((r) => {
                newState.enabledResources[r.name] = r;
            });
        }

        return newState;
    }

    static defaultProps = {
        confirm: () => {
        },
        intl: Object,
        bounds: undefined,
        onEditStart: undefined,
        onEditEnd: undefined,
    };

    constructor(props: Props) {
        super(props);
        this.featureLayers = [];
        props.resources.forEach((r) => {
            this.featureGroups[r.name] = React.createRef();
            this.callbacks[r.name] = {
                onCreated: this.handleCreated.bind(this, r),
            };
            this.editControlRefs[r.name] = React.createRef();
        });

        this.state = {
            creatingObject: null,
            updatingObject: null,
            editingLayer: null,
            enabledResources: {},
            errMessages: null,
            propsResources: [],
            loading: false,
            queryLoading: false,
            showMapEditForm: false,
            // eslint-disable-next-line react/no-unused-state
            // showSidePaneInfo: true,
            drawnLayer: null,
        };

        this.map = React.createRef();
    }

    componentDidMount() {
        // wait for all visible feature groups to mount
        let retries = 100;

        const checkMounted = () => {
            let mounted = null;
            if (retries > 0) {
                mounted = Object.keys(this.featureGroups).find((k) => !!this.featureGroups[k].current);
            }
            if (mounted) {
                this._loadFeatures();
            } else {
                retries -= 1;
                setTimeout(checkMounted, 100);
            }
        };
        checkMounted();
        const { setPosition } = this.props;
        if (setPosition) {
            this.map.current.leafletElement.on('moveend',
                () => setPosition(
                    this.map.current.leafletElement.getCenter(),
                    this.map.current.leafletElement.getZoom(),
                ));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { resources, onEditEnd, onEditStart } = this.props;
        resources.forEach((r) => {
            this.callbacks[r.name] = {
                onCreated: this.handleCreated.bind(this, r),
            };
        });

        const { resources: previousResources } = prevProps;
        if (resources !== previousResources) {
            this._loadFeatures(true);
        }

        const { editingLayer } = this.state;
        if (prevState.editingLayer !== editingLayer) {
            if (onEditEnd && !editingLayer && prevState.editingLayer) {
                onEditEnd(prevState.editingLayer.layer);
            }
            if (onEditStart && editingLayer) {
                onEditStart(editingLayer.layer);
            }
        }
        this.map.current.leafletElement.invalidateSize();
    }

    setIsSubmitting = (isSubmitting) => {
        this.setState({
            queryLoading: isSubmitting,
        });
    };


    focusLayer = (layer) => {
        this.map.current.leafletElement.panInsideBounds(layer.getBounds());
    };

    _loadFeatures = (clear = false) => {
        if (this.map.current) {
            const {
                getData, history, match, openPanel,
            } = this.props;
            const {
                enabledResources, editingLayer, creatingLayer, drawing,
            } = this.state;
            const currentEditedResource = editingLayer ? editingLayer.resource : null;

            this.setState({ loading: true }, () => {
                const { setMapLoading } = this.props;
                if (setMapLoading) setMapLoading(true);
            });

            getData(
                Object.values(enabledResources).filter((r) => (
                    (!currentEditedResource || (r.name !== currentEditedResource.name))
                    && this.featureGroups[r.name].current
                )),
                // $FlowFixMe
                this.map.current.leafletElement.getBounds().toBBoxString(),
                this.filters,
            ).then((results) => {
                results.forEach((result) => {
                    if (!this.featureGroups[result.resource.name].current) {
                        return;
                    }

                    const options = {};

                    if (result.resource.displayFields) {
                        options.onEachFeature = (feature, layer) => {
                            let tooltipContent;

                            if (result.resource.tooltipContent) {
                                // eslint-disable-next-line prefer-destructuring
                                tooltipContent = result.resource.tooltipContent;
                            } else {
                                tooltipContent = this._getFeatureTitle(result.resource, feature);
                            }

                            if (typeof result.resource.additionalOptions === typeof Function) {
                                layer.options = {
                                    ...layer.options,
                                    ...result.resource.additionalOptions(feature, layer),
                                };
                            }

                            let oldStyles = {};
                            layer.on('mouseover', () => {
                                oldStyles = { ...layer.options };
                                const newStyle = layer.options;

                                switch (result.resource.type) {
                                case 'polygon':
                                    newStyle.fillOpacity *= 1.25;
                                    newStyle.weight *= 2;
                                    layer.setStyle(newStyle);
                                    break;
                                case 'polyline':
                                    newStyle.weight *= 2;
                                    layer.setStyle(newStyle);
                                    break;
                                default:
                                    break;
                                }
                            });
                            layer.on('mouseout', () => {
                                switch (result.resource.type) {
                                case 'point':
                                    break;
                                default:
                                    layer.setStyle(oldStyles);
                                }
                            });

                            layer.bindTooltip(tooltipContent, { className: 'tooltip' });
                            if (!drawing && layer.feature.properties.type === 'coverage') {
                                layer.on('click', () => {
                                    if (openPanel) {
                                        openPanel(MAP_SIDE_CONTAINER_KEY);
                                    }
                                    history.push(`${match.url}/${layer.feature.id}`);
                                });
                            }
                        };
                    }

                    const leafletGeoJSON = new window.L.GeoJSON(result.results, options);
                    const leafletFG = this.featureGroups[result.resource.name].current.leafletElement;

                    if (clear) {
                        if (creatingLayer) {
                            // Disable pm so that the added layers below are not in edit mode, this is the case
                            // because in Draw mode with enabled pm all added layers enter edit mode
                            creatingLayer.layer.pm.disable();
                            leafletFG.eachLayer((layer) => {
                                if (layer !== creatingLayer.layer) {
                                    leafletFG.removeLayer(layer);
                                }
                            });
                        } else {
                            leafletFG.clearLayers();
                        }
                    }

                    leafletGeoJSON.eachLayer((layer) => {
                        leafletFG.addLayer(layer);
                    });

                    if (creatingLayer) {
                        creatingLayer.layer.pm.enable();
                    }
                });

                this.setState({ loading: false }, () => {
                    const { setMapLoading } = this.props;
                    if (setMapLoading) setMapLoading(false);
                });
            });
        }
    };

    _getFeatureTitle = (resource: Resource, feature: Object) => {
        const transformed = resource.displayFields ? resource.displayFields.map(
            (f) => feature.properties[f],
        ).filter((f) => !!f) : [];

        return transformed.join(resource.displaySeparator || ', ');
    };

    handleViewportChanged = () => {
        this._loadFeatures(true);
    };

    handleEditClose = () => {
        const { drawnLayer } = this.state;
        if (drawnLayer) {
            drawnLayer.remove();
        }

        this.setDrawing(false);
        this.closeSidePanel();
        this.setState({
            updatingObject: null,
            showMapEditForm: false,
            editingLayer: null,
        }, this._loadFeatures(true));
    };

    handleCreateClose = () => {
        const { creatingObject } = this.state;
        const { closePanel } = this.props;
        if (closePanel) {
            closePanel(CREATE_MAP_SIDE_CONTAINER_KEY, false);
        }
        this.setState({
            creatingLayer: { layer: creatingObject.layer, resource: creatingObject.resource },
            showMapEditForm: false,
            drawnLayer: creatingObject.layer,
        }, () => {
            if (creatingObject.resource.type.code === 'point') {
                creatingObject.layer._map.pm.Draw.Marker.disable();
            }
        });
    };

    handleCreateSave = () => {
        this._loadFeatures(true);
        this.setDrawing(false);
        this.closeSidePanel();
        this.setState((prevState) => ({
            drawnLayer: prevState.creatingObject ? prevState.creatingObject.layer : null,
            creatingObject: null,
            updatingObject: null,
            showMapEditForm: false,
            queryLoading: false,
            editingLayer: null,
        }));
    };

    handleEnabledResourcesChange = (resource: Resource, enabled: boolean) => {
        this.setState((prevState) => {
            const enabledResources = { ...prevState.enabledResources };

            if (enabled) {
                enabledResources[resource.name] = resource;
            } else {
                delete enabledResources[resource.name];
            }

            return { enabledResources };
        }, () => {
            this._loadFeatures(true);
        });
    };

    handleFiltersChange = (resource: Resource, values: Object) => {
        this.filters[resource.name] = values;
        this._loadFeatures(true);
    };

    handleEdited = () => {
        const { updateObject } = this.props;
        const { editingLayer, updatingObject } = this.state;
        if (updatingObject) {
            this.handleEnabledResourcesChange(updatingObject.resource, true);
            return updateObject(
                updatingObject.resource,
                updatingObject.layer.feature.id,
                updatingObject.layer.toGeoJSON().geometry,
            ).then((err) => {
                const errMessages = err.data && err.data.non_field_errors
                    ? err.data.non_field_errors.join(' ') : null;
                this.setState({
                    queryLoading: false,
                    errMessages,
                    updatingObject: errMessages ? editingLayer : null,
                });
                this._loadFeatures(true);
                return !errMessages;
            });
        }
        this.setState({ updatingObject: editingLayer });

        return false;
    };

    handleEditedCancel = () => {
        const { creatingObject } = this.state;
        creatingObject.layer.remove();
        this.setState({
            creatingObject: null,
            creatingLayer: null,
            editingLayer: null,
        }, () => this._loadFeatures(true));
    };


    handleErrorMessageClose = () => {
        this.setState({ errMessages: null });
    };

    storeInitial = (values, formik) => {
        delete values.polygon;
        this.setState((prevState) => ({ creatingObject: { ...prevState.creatingObject, initial: values } }));
    };

    setDrawing = (drawing) => {
        this.setState({ drawing }, () => drawing && this._loadFeatures(true));
    };

    setFeatureLayers = (map) => {
        Object.values(map._layers).forEach((layer) => {
            if (layer.feature) {
                const findLayer = this.featureLayers
                    .find((l) => layer.feature.id === l.feature.id);
                if (findLayer) {
                    this.featureLayers
                        .splice(this.featureLayers
                            .indexOf(findLayer), 1);
                }
                this.featureLayers.push(layer);
            }
        });
    };

    setShowMapEditForm = (boolean) => {
        this.setState({ showMapEditForm: boolean });
    };

    closeSidePanel = () => {
        const { closePanel, match, history } = this.props;
        if (closePanel) {
            closePanel(MAP_SIDE_CONTAINER_KEY, false);
            const path = match.url.split('maps');
            history.push(`${path[0]}maps`);
        }
    };

    handleEditLayerClick(resource: Resource, layer: Object, e: SyntheticMouseEvent<HTMLButtonElement>) {
        const { drawnLayer } = this.state;
        if (drawnLayer) {
            drawnLayer.remove();
        }
        e.preventDefault();
        this.setState({
            editingLayer: { layer, resource },
            showMapEditForm: true,
        });
    }

    handleEditClick(resource: Resource, layer: Object, e: SyntheticMouseEvent<HTMLButtonElement>) {
        e.preventDefault();
        this.setState({
            updatingObject: { layer, resource },
            showMapEditForm: true,
        });
    }

    handleCreated(resource: Resource, e: Object) {
        const fg = this.featureGroups[resource.name].current;
        if (fg) {
            e.layer.addTo(this.map.current.leafletElement);
        }

        if (resource.getInitial) {
            resource.getInitial(e).then((initial) => {
                this.setState({
                    creatingObject: {
                        layer: e.layer,
                        resource,
                        initial,
                    },
                });
            });
        } else {
            this.setState({
                creatingObject: {
                    layer: e.layer,
                    resource,
                },
            });
        }
    }

    handleDeleteClick(resource: Resource, layer: Object, e: SyntheticMouseEvent<HTMLButtonElement>) {
        const { confirm, intl: { formatMessage } } = this.props;
        const { creatingLayer, creatingObject, drawnLayer } = this.state;
        let confirmm = 0;

        confirm({
            message: `${formatMessage(messages.question)} ${this._getFeatureTitle(resource, layer.feature)}`,
            actions: {
                cancel: {
                    label: formatMessage(messages.no),
                    callback: () => {
                    },
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(messages.yes),
                    callback: () => {
                        confirmm += 1;
                        const { deleteObject } = this.props;
                        this.setState({ queryLoading: true });
                        deleteObject(resource, layer.feature.id).then((res) => {
                            this.setState({ queryLoading: false });
                            if (res.status === HTTP_400_BAD_REQUEST) {
                                confirm({
                                    message: res.data[0],
                                    actions: {
                                        cancel: {
                                            label: formatMessage(messages.cancelDelete),
                                            callback: () => {
                                            },
                                            type: 'negative',
                                        },
                                    },
                                });
                            } else {
                                if (drawnLayer) {
                                    drawnLayer.remove();
                                }
                                layer.remove();
                                // this.map.current.leafletElement.removeLayer(layer);
                                this.closeSidePanel();
                                this.setState({
                                    showMapEditForm: false,
                                    // drawedLayer: false,
                                },
                                () => this._loadFeatures(true));
                            }
                        });
                    },
                    type: 'positive',
                },
            },
        });
    }

    render() {
        const {
            center, resources, intl: { formatMessage }, bounds, match,
        } = this.props;
        const {
            creatingObject, enabledResources, creatingLayer, errMessages, loading, queryLoading, showMapEditForm,
        } = this.state;

        return (
            <React.Fragment>
                <Spinner loading={queryLoading} />
                <Spinner
                    type="clipLoader"
                    loading={loading}
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'absolute',
                        width: 'auto',
                        height: 'auto',
                        left: '60px',
                        top: '60px',
                    }}
                />
                <EditorResourceSelector
                    enabledResources={enabledResources}
                    resources={resources}
                    onEnabledChange={this.handleEnabledResourcesChange}
                    onFiltersChange={this.handleFiltersChange}
                />
                <Map
                    center={center}
                    zoom={7}
                    zoomControl={false}
                    style={{ height: 'calc(100% - 40px)' }}
                    onViewportChanged={this.handleViewportChanged}
                    ref={this.map}
                >
                    <ZoomControl />
                    {bounds && (
                        <Polygon
                            positions={bounds.map((c) => [c[1], c[0]])}
                            pmIgnore
                            color="#6d7382"
                            fill={false}
                        />
                    )}
                    <TileLayer
                        attribution={
                            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        }
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {
                        Object.keys(enabledResources).map((key) => {
                            const r = enabledResources[key];
                            const label = typeof r.label === 'string'
                                ? r.label : formatMessage(r.label);

                            const labelPlural = typeof r.label === 'string'
                                ? r.label : formatMessage(r.label);
                            const group = (
                                // $FlowFixMe
                                <FeatureGroup
                                    ref={this.featureGroups[r.name]}
                                    key={`${r.name}-${r.key}`}
                                >
                                    {r.allowNew && (
                                        <DrawControl
                                            title={`${formatMessage(messages.add)} ${label}`}
                                            onCreated={this.callbacks[r.name].onCreated}
                                            {...DRAW_TYPES_SETTINGS[r.type]}
                                            setDrawing={this.setDrawing}
                                        />
                                    )}
                                </FeatureGroup>
                            );

                            if (typeof r.minZoom !== 'undefined' || typeof r.maxZoom !== 'undefined') {
                                return (
                                    <ZoomRestricted minZoom={r.minZoom} maxZoom={r.maxZoom} key={r.name}>
                                        {group}
                                    </ZoomRestricted>
                                );
                            }
                            return group;
                        })
                    }
                    <LeafletConsumer>
                        {
                            ({ map }) => (
                                <Switch>
                                    <Route
                                        path={`${match.path}/:objectId/`}
                                        render={(props) => {
                                            this.setFeatureLayers(map);

                                            if (!isEmpty(this.featureLayers)) {
                                                const currLayer = Object.values(this.featureLayers)
                                                    .find((layer) => (
                                                        layer.feature
                                                        && layer.feature.id === props.match.params.objectId
                                                    ));
                                                const resource = resources.find((res) => res.name === 'address.map');
                                                return (
                                                    <MapSidePaneContainer
                                                        id={MAP_SIDE_CONTAINER_KEY}
                                                        currLayer={currLayer}
                                                        resource={resource}
                                                        loading={loading}
                                                        editor={this}
                                                        onClose={this.closeSidePanel}
                                                        showMapEditForm={showMapEditForm}
                                                        setShowMapEditForm={this.setShowMapEditForm}
                                                        formatMessage={formatMessage}
                                                        messages={messages}
                                                    />
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                </Switch>
                            )
                        }
                    </LeafletConsumer>
                </Map>
                {
                    (creatingObject && !creatingLayer) && (
                        <SidePane
                            id={CREATE_MAP_SIDE_CONTAINER_KEY}
                            openInitially
                            onClose={this.handleCreateClose}
                            title={`${formatMessage(messages['create-save'])} ${
                                typeof creatingObject.resource.label === 'string'
                                    ? creatingObject.resource.label
                                    : formatMessage(creatingObject.resource.label)
                            }`}
                        >
                            <ApiForm
                                resourceName={creatingObject.resource.name}
                                resourceArguments={creatingObject.resource.arguments}
                                excludeFields={[creatingObject.resource.geometryField]}
                                initial={{
                                    [creatingObject.resource.geometryField]:
                                    creatingObject.layer.toGeoJSON().geometry,
                                    ...creatingObject.initial,
                                }}
                                onSave={this.handleCreateSave}
                                onSubmit={(values, formik) => {
                                    this.setIsSubmitting(true);
                                    return this.storeInitial(values, formik);
                                }}
                                onClose={this.handleEditedCancel}
                                onError={(res) => this.setIsSubmitting(false)}
                                height="100%"
                            />
                        </SidePane>
                    )
                }
                {
                    errMessages ? (
                        <MessageDialog
                            message={errMessages}
                            actions={{
                                ok: {
                                    label: formatMessage(messages.error),
                                    callback: this.handleErrorMessageClose,
                                    type: 'positive',
                                },
                            }}
                            handleClose={this.handleErrorMessageClose}
                        />
                    ) : null
                }
            </React.Fragment>
        );
    }
}


export default injectIntl(Editor, { forwardRef: true });
